import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import BrandFooter from "../components/BrandFooter";

function challengeCopy(type) {
  if (type === "email_verification_first_login") {
    return {
      title: "Please verify your email address to continue.",
      helper: "This first-login OTP verifies the email address on your account and then completes sign-in.",
      button: "Verify and sign in",
    };
  }
  return {
    title: "Enter the login OTP sent to your email.",
    helper: "Password verified. Complete this email OTP step to access QualiGate.",
    button: "Verify OTP",
  };
}

export default function Login() {
  const { login, loading, otpChallenge, verifyLoginOtp, resendLoginOtp, clearOtpChallenge } = useAuth();
  const navigate = useNavigate();
  const [uid, setUid] = useState("");
  const [pwd, setPwd] = useState("");
  const [otp, setOtp] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [canResendAt, setCanResendAt] = useState(0);
  const [, setTick] = useState(0);

  const cooldown = Math.max(0, Math.ceil((canResendAt - Date.now()) / 1000));
  const copy = useMemo(() => challengeCopy(otpChallenge?.challenge_type), [otpChallenge?.challenge_type]);

  useEffect(() => {
    document.title = "QualiGate — Sign in";
  }, []);

  useEffect(() => {
    if (!otpChallenge || cooldown <= 0) return undefined;
    const id = window.setInterval(() => setTick((value) => value + 1), 250);
    return () => window.clearInterval(id);
  }, [otpChallenge, cooldown]);

  const startCooldown = (challenge) => {
    const seconds = Number(challenge?.resend_cooldown_seconds ?? 60);
    setCanResendAt(Date.now() + Math.max(0, seconds) * 1000);
  };

  const onSubmitCredentials = async (event) => {
    event.preventDefault();
    setErr("");
    setInfo("");
    const result = await login(uid, pwd);
    if (!result.ok) {
      setErr(result.error || "Invalid credentials");
      return;
    }
    if (result.otpRequired) {
      setOtp("");
      startCooldown(result.challenge);
      setInfo(`OTP sent to ${result.challenge?.email || "your email address"}.`);
      return;
    }
    navigate("/");
  };

  const onVerifyOtp = async (event) => {
    event.preventDefault();
    if (!otpChallenge?.challenge_id) {
      setErr("Login challenge expired. Please sign in again.");
      return;
    }
    setErr("");
    setInfo("");
    const result = await verifyLoginOtp(otpChallenge.challenge_id, otp);
    if (!result.ok) {
      setErr(result.error || "Invalid OTP");
      return;
    }
    navigate("/");
  };

  const resendOtp = async () => {
    if (cooldown > 0 || !otpChallenge?.challenge_id) return;
    setErr("");
    setInfo("");
    const result = await resendLoginOtp(otpChallenge.challenge_id);
    if (!result.ok) {
      setErr(result.error || "Could not resend OTP");
      return;
    }
    setOtp("");
    startCooldown(result.challenge);
    setInfo(`A fresh OTP was sent to ${result.challenge?.email || otpChallenge.email || "your email address"}.`);
  };

  const backToCredentials = () => {
    clearOtpChallenge?.();
    setOtp("");
    setErr("");
    setInfo("");
  };

  return (
    <div className="login-shell">
      <div className="login-backdrop-orb login-backdrop-orb--left" />
      <div className="login-backdrop-orb login-backdrop-orb--right" />
      <div className="login-banner" aria-hidden="true" />

      <div className="login-card-wrap">
        <div className="login-card-shell premium-fade-in">
          <form onSubmit={otpChallenge ? onVerifyOtp : onSubmitCredentials} className="login-card login-form-card">
            <div className="login-client-logo-wrap" aria-label="Client logo">
              <img
                src="/brand/client.png"
                alt="Client logo"
                className="login-client-logo"
                onError={(event) => {
                  if (event.currentTarget.dataset.fallbackTried === "1") {
                    event.currentTarget.style.display = "none";
                    return;
                  }
                  event.currentTarget.dataset.fallbackTried = "1";
                  event.currentTarget.src = "/brand/logo.png";
                }}
              />
            </div>

            {!otpChallenge ? (
              <>
                <div className="login-subcopy">Sign in to continue.</div>
                <div className="vstack">
                  <label>
                    <span>User ID</span>
                    <input value={uid} onChange={(e) => setUid(e.target.value)} autoFocus placeholder="Enter your user ID" disabled={loading} />
                  </label>
                  <label>
                    <span>Password</span>
                    <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Enter your password" disabled={loading} />
                  </label>
                  {err ? <div className="banner error">{err}</div> : null}
                  <button className="btn btn-primary login-submit-btn" type="submit" disabled={loading || !uid || !pwd}>
                    {loading ? "Checking…" : "Sign in"}
                  </button>
                </div>

                <div className="login-helper-row">
                  <Link className="text-link login-helper-link" to="/forgot-password">Forgot password?</Link>
                </div>
              </>
            ) : (
              <>
                <div className="login-subcopy">{copy.title}</div>
                <div className="helper-text auth-helper-copy">{copy.helper}</div>
                <div className="auth-inline-note">
                  OTP sent to <strong>{otpChallenge.email || "your email address"}</strong>
                  {otpChallenge.expires_in_seconds ? ` and expires in about ${Math.ceil(Number(otpChallenge.expires_in_seconds) / 60)} minute(s).` : "."}
                </div>
                <div className="auth-inline-note">Didn’t receive the email? Check your spam folder or request a fresh OTP.</div>
                <div className="vstack">
                  <label>
                    <span>6-digit OTP</span>
                    <input
                      autoFocus
                      placeholder="Enter the OTP"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={otp}
                      onChange={(event) => setOtp(event.target.value.replace(/[^\d]/g, "").slice(0, 6))}
                      disabled={loading}
                    />
                  </label>
                  {info ? <div className="banner success">{info}</div> : null}
                  {err ? <div className="banner error">{err}</div> : null}
                  <button className="btn btn-primary login-submit-btn" type="submit" disabled={loading || otp.length !== 6}>
                    {loading ? "Verifying…" : copy.button}
                  </button>
                  <div className="auth-action-row auth-action-row--split">
                    <button className="btn btn-secondary" type="button" onClick={backToCredentials} disabled={loading}>Back</button>
                    <button className="btn btn-secondary" type="button" onClick={resendOtp} disabled={loading || cooldown > 0}>
                      {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </form>
        </div>
        <BrandFooter className="login-page-footer" />
      </div>
    </div>
  );
}
